<script setup lang="ts">
import {computed, watch} from "vue";
import {CircleAlert, History} from "@lucide/vue";
import {storeToRefs} from "pinia";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import AssistantPropositionCard from "@/ai-assistant/components/AssistantPropositionCard.vue";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import useTypedText from "@/ai-assistant/composables/useTypedText";
import {hasMoved, screenLabel} from "@/ai-assistant/lib/screenContext";
import {AssistantMessage, Proposition} from "@/ai-assistant/interfaces";

const {message} = defineProps<{
    message: AssistantMessage;
}>();

const emits = defineEmits<{
    (event: 'adjust', proposition: Proposition): void;
    (event: 'grow'): void;
}>();

const {screen, revealRequests, error} = storeToRefs(useAiAssistantStore());

/**
 * Only an answer caught while it is being received is written at pace.
 *
 * Decided once, when the bubble appears, and it holds for the message's whole
 * life: the drawer is hidden rather than unmounted, so closing it does not
 * interrupt an answer being written and reopening finds it where it was. A
 * layout torn down and rebuilt — signing out — finds every message finished.
 *
 * The pace outlives the stream on purpose: the server sends the whole answer
 * in one breath, so the stream is over long before the writing is.
 */
const {displayed, reveal} = useTypedText(
    () => message.content,
    message.role === 'assistant' && message.isStreaming === true
);

/**
 * Whether the answer is entirely on screen.
 *
 * What hangs off an answer waits for it: the scenarios land while the text is
 * still being written, and shown at once they would sit under a sentence the
 * pharmacist cannot read yet — with the panel scrolled to the bottom of the
 * cards, leaving that sentence above the fold.
 */
const written = computed<boolean>(() => displayed.value === message.content);

// A click elsewhere, a new question, or a failure: the pace gives way.
watch(revealRequests, reveal);
watch(error, cause => cause && reveal());

// The conversation follows the text as the source grows; it has to follow the
// view too, which grows later.
watch(displayed, () => emits('grow'));

/**
 * The screen this answer was computed for, named only once it is no longer the
 * one in front of the pharmacist.
 *
 * Said rather than hidden. The answer was asked for and remains worth reading;
 * what would be wrong is letting a scenario built on last week pass for advice
 * about the week now on display.
 */
const askedFrom = computed<string | null>(() =>
    message.role === 'assistant' && hasMoved(message.screen, screen.value)
        ? screenLabel(message.screen)
        : null
);
</script>

<template>
  <div class="flex flex-col gap-2" :class="message.role === 'user' ? 'items-end' : 'items-start'">
    <div
      v-if="message.role === 'user'"
      class="max-w-[85%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-xs leading-relaxed text-white"
    >
      {{ message.content }}
    </div>
    <!--
      An answer still being written says so and can be taken whole: the pace
      is a comfort, and the pharmacist who has read enough must not be made to
      wait for the rest. Reachable from the keyboard too, where the composer
      is the only other way out and it takes the focus off what is being read.
    -->
    <div
      v-else
      class="max-w-[92%] rounded-lg bg-gray-light px-3 py-2 text-xs leading-relaxed text-gray-dark"
      :class="{'cursor-pointer': !written}"
      :role="written ? undefined : 'button'"
      :tabindex="written ? undefined : 0"
      :title="written ? undefined : 'Afficher la réponse entière'"
      @click="reveal"
      @keydown.enter="reveal"
      @keydown.space.prevent="reveal"
    >
      <!-- Written by a model: HTML in it is displayed, never executed. -->
      <MarkdownView
        v-if="displayed"
        :content="displayed"
        class="!p-0 text-xs"
        inherit-styles
        untrusted
      />
      <div v-else class="flex items-center gap-1 py-1" aria-label="L'assistant rédige sa réponse">
        <span v-for="dot in 3" :key="dot" class="size-1.5 animate-pulse rounded-full bg-primary/40" />
      </div>
    </div>

    <p
      v-if="askedFrom && displayed"
      class="flex max-w-[92%] items-start gap-1 text-[10.5px] leading-snug text-gray-dark/60"
    >
      <History class="mt-px size-3 shrink-0" />
      <span>Calculée pour {{ askedFrom }}, pas pour ce que vous avez à l'écran.</span>
    </p>

    <!--
      The scenarios sit under the answer that introduced them rather than in a
      panel of their own: a proposition only means something beside the
      question it answers, and the transcript is what gets scrolled back.
      Wider than the bubble, because a table of impacts needs the room.
    -->
    <p
      v-if="message.propositionsLoading && written"
      class="w-full text-[11px] text-gray-dark/60"
    >
      Chargement des scénarios…
    </p>

    <div v-if="message.propositions?.length && written" class="w-full space-y-2">
      <AssistantPropositionCard
        v-for="(proposition, index) in message.propositions"
        :key="proposition.id"
        :proposition="proposition"
        :index="index"
        @adjust="emits('adjust', $event)"
      />
    </div>

    <!-- Beside the answer it concerns, so it dies with it. -->
    <p
      v-if="message.propositionsError && written"
      class="flex w-full items-start gap-1.5 text-[11px] leading-snug text-gray-dark/70"
    >
      <CircleAlert class="mt-px size-3 shrink-0 text-orange" />
      {{ message.propositionsError }}
    </p>
  </div>
</template>
